import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Zap, QrCode, Wallet, ArrowRightLeft, LineChart,
  Lock, BadgeCheck, ArrowRight, IndianRupee, Smartphone, Download,
} from "lucide-react";

const ticker = [
  "₹2,500 received", "₹18,000 payout approved", "₹750 received", "₹1,20,000 settled",
  "₹499 received", "₹9,999 received", "₹3,200 payout", "₹45,000 received",
];

const features = [
  { icon: QrCode, title: "Static UPI QR Collect", desc: "Assign each client a branded static QR & UPI ID. Customers scan with any UPI app and pay instantly." },
  { icon: Wallet, title: "Triple Wallet System", desc: "Dedicated Collection, Payout and Chargeback wallets give you full control over every rupee that moves." },
  { icon: ArrowRightLeft, title: "Instant Fund Transfers", desc: "Move balance between collection and payout wallets and settle beneficiaries in seconds." },
  { icon: LineChart, title: "Live Analytics", desc: "Real-time pay-in vs payout graphs, total collection trends and status breakdowns on one dashboard." },
  { icon: Zap, title: "Realtime + Sound Alerts", desc: "Dashboards auto-refresh and chime the moment a payment lands — no page reload needed." },
  { icon: Download, title: "PDF Slips & Statements", desc: "Download professional payment receipts and full account statements as PDF or CSV anytime." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-xl bg-blue-600 grid place-items-center font-heading font-extrabold">V</div>
            <span className="font-heading text-xl font-extrabold tracking-tight">NaxoPay</span>
          </div>
          <Button data-testid="nav-login-btn" onClick={() => navigate("/login")} className="rounded-full bg-blue-600 hover:bg-blue-700 px-5">
            Sign in <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.25),transparent)]" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300 mb-6">
              <span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" /> Live UPI collections & payouts
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              The payment gateway built for <span className="text-blue-500">static QR</span> collections
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              NaxoPay gives your business a complete UPI collection & payout stack — branded QR codes, triple wallets,
              instant settlements, realtime alerts and downloadable statements.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-get-started-btn" onClick={() => navigate("/login")} className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
                Get started <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-400 px-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400" /> Bank-grade secure
              </div>
            </div>
          </motion.div>

          {/* Ticker */}
          <div className="mt-14 relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 py-3">
            <div className="flex gap-8 whitespace-nowrap animate-ticker w-max">
              {[...ticker, ...ticker].map((t, i) => (
                <span key={i} className="flex items-center gap-2 text-sm text-slate-300 font-mono-data">
                  <IndianRupee className="h-3.5 w-3.5 text-emerald-400" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">Everything you need to move money</h2>
          <p className="mt-3 text-slate-400 text-center max-w-2xl mx-auto">A full-featured gateway experience — collection, payouts and reconciliation in one place.</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] hover:border-blue-500/30 transition-all"
                data-testid={`feature-card-${i}`}
              >
                <div className="h-11 w-11 rounded-xl bg-blue-600/20 text-blue-400 grid place-items-center mb-4">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlight strip */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-5">
          {[
            { icon: Smartphone, t: "Mobile-first QR", d: "Beautiful collect screen on every phone" },
            { icon: BadgeCheck, t: "Manual UTR verify", d: "Every credit backed by a 12-digit UTR" },
            { icon: Lock, t: "OTP protected login", d: "Email one-time codes for every client" },
          ].map((x, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-gradient-to-br from-blue-950/40 to-slate-900 p-6 flex items-start gap-4">
              <x.icon className="h-6 w-6 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-semibold">{x.t}</div>
                <div className="text-sm text-slate-400 mt-1">{x.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-blue-600 grid place-items-center font-heading font-extrabold text-white text-sm">V</div>
            <span className="font-heading font-bold text-slate-300">NaxoPay</span>
          </div>
          <span>© {new Date().getFullYear()} NaxoPay. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
